import type { CustomerDTO } from "@ifa/utils";

import type { FinancialSnapshot } from "./fnaBasicConfig.js";

type CustomerFinancial = CustomerDTO["financial"] | null | undefined;

function toNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function buildBaseSnapshot(financial: CustomerFinancial): FinancialSnapshot | undefined {
  if (!financial) return undefined;

  return {
    income: financial.income,
    expense: financial.expense,
    currentAssets: financial.currentAssets,
    nonCurrentAssets: financial.nonCurrentAssets,
    liabilities: financial.liabilities,
    protection: financial.protection,
  };
}

export function resolveFinancialSnapshotForDisplay(
  financial: CustomerFinancial,
  isZuuFinancialStepMode: boolean,
): FinancialSnapshot | undefined {
  const baseSnapshot = buildBaseSnapshot(financial);
  if (!baseSnapshot || !isZuuFinancialStepMode) {
    return baseSnapshot;
  }

  const monthlyIncomesTotal =
    toNumber((financial as any)?.monthlyIncomesTotal) ??
    toNumber(financial?.income?.avgMonthlyIncome);
  const monthlyExpensesTotal =
    toNumber((financial as any)?.monthlyExpensesTotal) ??
    toNumber(financial?.expense?.avgMonthlyExpense);
  const liquidAssetsTotal =
    toNumber((financial as any)?.liquidAssetsTotal) ??
    toNumber(financial?.currentAssets?.currentLiquidAssets);

  return {
    income:
      monthlyIncomesTotal !== undefined
        ? {
            salaryCommissionBonus: monthlyIncomesTotal,
            rentalIncome: 0,
            hasOtherIncome: false,
            otherIncomeName: undefined,
            otherIncomeAmount: undefined,
          }
        : baseSnapshot.income,
    expense:
      monthlyExpensesTotal !== undefined
        ? {
            personalAndFamilyExpense: monthlyExpensesTotal,
            mortgageLoanRepayment: 0,
            insuranceContribution: 0,
            hasOtherExpense: false,
            otherExpenseName: undefined,
            otherExpenseAmount: undefined,
          }
        : baseSnapshot.expense,
    currentAssets:
      liquidAssetsTotal !== undefined
        ? {
            cashAndDeposits: liquidAssetsTotal,
            liquidInvestments: 0,
            hasOtherCurrentAssets: false,
            otherCurrentAssetsName: undefined,
            otherCurrentAssetsAmount: undefined,
          }
        : baseSnapshot.currentAssets,
    nonCurrentAssets: baseSnapshot.nonCurrentAssets,
    liabilities: baseSnapshot.liabilities,
    protection: baseSnapshot.protection,
  };
}
